
import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { ChevronLeft, ChevronRight, Save } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';

import BasicInfoStep from './BasicInfoStep';
import DetailedInfoStep from './DetailedInfoStep';
import FeaturesStep from './FeaturesStep';
import ContactLocationStep from './ContactLocationStep';
import ImagesStep from './ImagesStep';

interface MultiStepPropertyFormProps {
  afterSubmit: () => void;
  editingProperty?: any;
  onCancel?: () => void;
}

const MultiStepPropertyForm = ({ afterSubmit, editingProperty, onCancel }: MultiStepPropertyFormProps) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  
  const [formData, setFormData] = useState({
    title: editingProperty?.title || '',
    type: editingProperty?.type || '',
    address: editingProperty?.address || '',
    monthly_price: editingProperty?.monthly_price || '',
    description: editingProperty?.description || '',
    detailed_description: editingProperty?.detailed_description || '',
    bedrooms: editingProperty?.bedrooms || 1,
    bathrooms: editingProperty?.bathrooms || 1,
    area: editingProperty?.area || 0,
    floor_number: editingProperty?.floor_number || null,
    building_age: editingProperty?.building_age || null,
    furnishing_status: editingProperty?.furnishing_status || 'unfurnished',
    features: editingProperty?.features || [],
    location_lat: editingProperty?.location_lat || null,
    location_lng: editingProperty?.location_lng || null,
    contact_phone: editingProperty?.contact_phone || '',
    contact_whatsapp: editingProperty?.contact_whatsapp || '',
    contact_email: editingProperty?.contact_email || '',
    available_from: editingProperty?.available_from || new Date().toISOString().split('T')[0],
    images: []
  });

  // تحميل الصور الموجودة عند التحديث
  useEffect(() => {
    if (editingProperty?.id) {
      loadExistingImages();
    }
  }, [editingProperty?.id]);

  const loadExistingImages = async () => {
    try {
      const { data, error } = await supabase
        .from('property_images')
        .select('*')
        .eq('property_id', editingProperty.id)
        .order('image_order', { ascending: true });

      if (error) {
        console.error('Error loading existing images:', error);
        return;
      }

      if (data && data.length > 0) {
        const existingImages = data.map(img => ({
          url: img.storage_path ? 
            supabase.storage.from('property-images').getPublicUrl(img.storage_path).data.publicUrl : 
            img.url,
          storage_path: img.storage_path,
          id: img.id,
          existing: true
        })).filter(img => img.url && !img.url.startsWith('blob:'));

        setFormData(prev => ({ ...prev, images: existingImages }));
        console.log('Loaded existing images:', existingImages);
      }
    } catch (error) {
      console.error('Error loading images:', error);
    }
  };

  const steps = [
    { id: 1, title: 'المعلومات الأساسية', component: BasicInfoStep },
    { id: 2, title: 'التفاصيل الكاملة', component: DetailedInfoStep },
    { id: 3, title: 'المميزات', component: FeaturesStep },
    { id: 4, title: 'التواصل والموقع', component: ContactLocationStep },
    { id: 5, title: 'الصور', component: ImagesStep }
  ];

  const handleChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateStep = (step: number): boolean => {
    const newErrors: Record<string, string> = {};

    if (step === 1) {
      if (!formData.title.trim()) newErrors.title = 'عنوان العقار مطلوب';
      if (!formData.type) newErrors.type = 'نوع العقار مطلوب';
      if (!formData.address.trim()) newErrors.address = 'العنوان مطلوب';
      if (!formData.monthly_price || parseFloat(formData.monthly_price.toString()) <= 0) {
        newErrors.monthly_price = 'السعر الشهري مطلوب ويجب أن يكون أكبر من صفر';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, steps.length));
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = async () => {
    if (!validateStep(1)) {
      setCurrentStep(1);
      return;
    }

    setLoading(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast({
          title: "خطأ",
          description: "يرجى تسجيل الدخول أولاً",
          variant: "destructive"
        });
        return;
      }

      const { data: landlordData } = await supabase
        .from('landlords')
        .select('id')
        .eq('user_id', user.id)
        .single();

      const propertyData = {
        owner_id: editingProperty?.owner_id || user.id,
        landlord_id: editingProperty?.landlord_id || landlordData?.id,
        title: formData.title,
        type: formData.type,
        address: formData.address,
        monthly_price: parseFloat(formData.monthly_price.toString()),
        description: formData.description,
        detailed_description: formData.detailed_description || null,
        bedrooms: parseInt(formData.bedrooms.toString()),
        bathrooms: parseInt(formData.bathrooms.toString()),
        area: parseFloat(formData.area.toString()) || 0,
        floor_number: formData.floor_number ? parseInt(formData.floor_number.toString()) : null,
        building_age: formData.building_age ? parseInt(formData.building_age.toString()) : null,
        furnishing_status: formData.furnishing_status,
        features: formData.features,
        location_lat: formData.location_lat,
        location_lng: formData.location_lng,
        contact_phone: formData.contact_phone || null,
        contact_whatsapp: formData.contact_whatsapp || null,
        contact_email: formData.contact_email || null,
        available_from: formData.available_from,
        occupied: false,
        is_active: true
      };

      let result;
      if (editingProperty) {
        result = await supabase
          .from("properties")
          .update(propertyData)
          .eq("id", editingProperty.id)
          .select()
          .single();
      } else {
        result = await supabase
          .from("properties")
          .insert([propertyData])
          .select()
          .single();
      }

      if (result.error) {
        throw result.error;
      }

      const propertyId = result.data.id;

      // حفظ الصور الجديدة فقط (غير الموجودة)
      const newImages = formData.images.filter(img => !img.existing);
      if (newImages.length > 0) {
        for (let i = 0; i < newImages.length; i++) {
          const image = newImages[i];
          const totalImageOrder = formData.images.length > 0 ? 
            formData.images.filter(img => img.existing).length + i : i;

          const imageData = {
            property_id: propertyId,
            url: image.url,
            storage_path: image.storage_path,
            image_order: totalImageOrder,
            is_main: totalImageOrder === 0,
            file_size: image.size || null,
            mime_type: image.type || null
          };

          const { error: imgError } = await supabase
            .from("property_images")
            .insert([imageData]);

          if (imgError) {
            console.error("خطأ في حفظ الصورة:", imgError);
          } else {
            console.log("تم حفظ الصورة بنجاح:", imageData);
          }
        }
      }

      toast({
        title: "نجح العملية",
        description: editingProperty ? "تم تحديث العقار بنجاح" : "تم إضافة العقار بنجاح",
        variant: "default"
      });

      afterSubmit();
      
      // إعادة تعيين النموذج للعقارات الجديدة
      if (!editingProperty) {
        setFormData({
          title: '', type: '', address: '', monthly_price: '', description: '',
          detailed_description: '', bedrooms: 1, bathrooms: 1, area: 0,
          floor_number: null, building_age: null, furnishing_status: 'unfurnished',
          features: [], location_lat: null, location_lng: null, contact_phone: '',
          contact_whatsapp: '', contact_email: '', 
          available_from: new Date().toISOString().split('T')[0], images: []
        });
        setCurrentStep(1);
      }

    } catch (error: any) {
      console.error('Error saving property:', error);
      toast({
        title: "خطأ",
        description: error.message || "حدث خطأ أثناء حفظ العقار",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const CurrentStepComponent = steps[currentStep - 1].component;
  const progress = (currentStep / steps.length) * 100;

  return (
    <div className="max-w-4xl mx-auto">
      {/* شريط التقدم */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="text-right">
            {editingProperty ? 'تحديث العقار' : 'إضافة عقار جديد'}
          </CardTitle>
          <div className="space-y-2">
            <div className="flex justify-between text-sm text-gray-600">
              <span>الخطوة {currentStep} من {steps.length}</span>
              <span>{steps[currentStep - 1].title}</span>
            </div>
            <Progress value={progress} className="w-full" />
          </div>
        </CardHeader>
      </Card>

      {/* خطوات النموذج */}
      <div className="mb-6">
        <div className="flex justify-center mb-4">
          <div className="flex space-x-reverse space-x-4">
            {steps.map((step) => (
              <div
                key={step.id}
                className={`flex items-center ${
                  step.id < steps.length ? 'flex-1' : ''
                }`}
              >
                <div
                  className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold ${
                    currentStep >= step.id
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-200 text-gray-600'
                  }`}
                >
                  {step.id}
                </div>
                <span className={`mr-2 text-sm ${
                  currentStep >= step.id ? 'text-blue-600 font-medium' : 'text-gray-500'
                }`}>
                  {step.title}
                </span>
                {step.id < steps.length && (
                  <div className={`flex-1 h-px mx-4 ${
                    currentStep > step.id ? 'bg-blue-600' : 'bg-gray-200'
                  }`} />
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* محتوى الخطوة الحالية */}
      <CurrentStepComponent
        data={formData}
        onChange={handleChange}
        errors={errors}
      />

      {/* أزرار التنقل */}
      <Card className="mt-6">
        <CardContent className="p-4">
          <div className="flex justify-between">
            <div className="flex gap-2">
              {onCancel && (
                <Button variant="outline" onClick={onCancel}>
                  إلغاء
                </Button>
              )}
              {currentStep > 1 && (
                <Button variant="outline" onClick={prevStep}>
                  <ChevronRight className="h-4 w-4 mr-1" />
                  السابق
                </Button>
              )}
            </div>
            
            <div className="flex gap-2">
              {currentStep < steps.length ? (
                <Button onClick={nextStep}>
                  التالي
                  <ChevronLeft className="h-4 w-4 ml-1" />
                </Button>
              ) : (
                <Button onClick={handleSubmit} disabled={loading}>
                  <Save className="h-4 w-4 mr-1" />
                  {loading ? 'جارٍ الحفظ...' : (editingProperty ? 'تحديث العقار' : 'إضافة العقار')}
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default MultiStepPropertyForm;
